import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Plus, Trash2, Save, ArrowLeft, Dumbbell, Search, X,
  ChevronDown, ChevronUp, Play, Lock, CreditCard, CheckCircle,
  GripVertical, Edit3
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";
const SPIERGROEPEN = ["Borst", "Rug", "Schouders", "Biceps", "Triceps", "Benen", "Billen", "Core", "Cardio", "Overig"];

// ── Exercise Picker ────────────────────────────────────────────────────────────
function ExercisePicker({ onSelect, onClose, userToken }) {
  const [query, setQuery] = useState("");
  const [libraryEx, setLibraryEx] = useState([]);
  const [myEx, setMyEx] = useState([]);
  const [tab, setTab] = useState("bibliotheek");
  const [newName, setNewName] = useState("");
  const [newGroep, setNewGroep] = useState("Overig");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/exercises`)
      .then(r => r.json())
      .then(d => setLibraryEx(d.exercises || []));
    fetch(`${API}/api/my-exercises`, { headers: { Authorization: `Bearer ${userToken}` } })
      .then(r => r.json())
      .then(d => setMyEx(d.exercises || []));
  }, [userToken]);

  const filtered = (tab === "bibliotheek" ? libraryEx : myEx).filter(e =>
    e.naam.toLowerCase().includes(query.toLowerCase())
  );

  const handleAddPrivate = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(`${API}/api/my-exercises`, {
        method: "POST",
        headers: { Authorization: `Bearer ${userToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ naam: newName.trim(), spiergroep: newGroep }),
      });
      const data = await res.json();
      if (data.id) {
        const ex = { id: data.id, naam: newName.trim(), spiergroep: newGroep };
        setMyEx(prev => [...prev, ex]);
        setNewName("");
        onSelect(ex.naam);
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
        style={{ background: "#111", border: "1px solid rgba(201,168,76,0.2)", maxHeight: "80vh" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between shrink-0">
          <p className="text-white font-bold">Oefening toevoegen</p>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/8 shrink-0">
          {["bibliotheek", "mijn oefeningen"].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                tab === t ? "text-[#C9A84C] border-b-2 border-[#C9A84C]" : "text-zinc-500 hover:text-zinc-300"
              }`}>
              {t}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="px-4 py-3 shrink-0">
          <div className="flex items-center gap-2 bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2">
            <Search size={14} className="text-zinc-500" />
            <input value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Zoek oefening..."
              className="flex-1 bg-transparent text-white text-sm focus:outline-none placeholder-zinc-600" />
          </div>
        </div>

        {/* Add private exercise (only on "mijn oefeningen" tab) */}
        {tab === "mijn oefeningen" && (
          <div className="px-4 pb-3 shrink-0">
            <div className="flex gap-2">
              <input value={newName} onChange={e => setNewName(e.target.value)}
                placeholder="Naam nieuwe oefening..."
                className="flex-1 bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40"
                onKeyDown={e => e.key === "Enter" && handleAddPrivate()} />
              <select value={newGroep} onChange={e => setNewGroep(e.target.value)}
                className="bg-[#1a1a1a] border border-white/8 rounded-xl px-2 py-2 text-white text-xs focus:outline-none">
                {SPIERGROEPEN.map(g => <option key={g}>{g}</option>)}
              </select>
              <button onClick={handleAddPrivate} disabled={adding || !newName.trim()}
                className="px-3 py-2 rounded-xl text-xs font-bold disabled:opacity-40"
                style={{ background: "rgba(201,168,76,0.15)", color: GOLD }}>
                {adding ? "..." : <Plus size={14} />}
              </button>
            </div>
          </div>
        )}

        {/* List */}
        <div className="overflow-y-auto flex-1 px-4 pb-4 space-y-1">
          {filtered.length === 0 ? (
            <p className="text-zinc-600 text-sm text-center py-8">Geen oefeningen gevonden</p>
          ) : filtered.map(e => (
            <button key={e.id || e.naam} onClick={() => onSelect(e.naam)}
              className="w-full text-left px-4 py-3 rounded-xl hover:bg-white/5 transition-colors flex items-center justify-between group">
              <div>
                <p className="text-white text-sm font-medium">{e.naam}</p>
                {e.spiergroep && <p className="text-zinc-600 text-xs">{e.spiergroep}</p>}
              </div>
              <Plus size={14} className="text-zinc-600 group-hover:text-[#C9A84C] transition-colors" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Dag Editor ────────────────────────────────────────────────────────────────
function DagEditor({ dag, dagIdx, onUpdate, onDelete, userToken }) {
  const [open, setOpen] = useState(true);
  const [showPicker, setShowPicker] = useState(false);

  const addOefening = (naam) => {
    const updated = {
      ...dag,
      oefeningen: [...dag.oefeningen, { naam, sets: "3", reps: "10", rust: "90 sec", notities: "" }]
    };
    onUpdate(updated);
    setShowPicker(false);
  };

  const updateOefening = (oeIdx, field, val) => {
    const updated = { ...dag, oefeningen: dag.oefeningen.map((o, i) => i === oeIdx ? { ...o, [field]: val } : o) };
    onUpdate(updated);
  };

  const removeOefening = (oeIdx) => {
    onUpdate({ ...dag, oefeningen: dag.oefeningen.filter((_, i) => i !== oeIdx) });
  };

  return (
    <div className="rounded-2xl border border-white/8 overflow-hidden" style={{ background: "#0f0f0f" }}>
      {/* Dag header */}
      <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/5">
        <GripVertical size={14} className="text-zinc-700 shrink-0" />
        <input
          value={dag.dag_naam}
          onChange={e => onUpdate({ ...dag, dag_naam: e.target.value })}
          className="flex-1 bg-transparent text-white font-bold text-sm focus:outline-none placeholder-zinc-600"
          placeholder="Naam van de dag (bijv. Dag A – Push)" />
        <button onClick={() => setOpen(o => !o)} className="text-zinc-500 hover:text-white p-1">
          {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
        <button onClick={onDelete} className="text-zinc-700 hover:text-red-400 p-1 transition-colors">
          <Trash2 size={14} />
        </button>
      </div>

      {open && (
        <div className="px-4 py-3 space-y-2">
          {dag.oefeningen.length === 0 ? (
            <p className="text-zinc-600 text-xs text-center py-3">Nog geen oefeningen. Voeg er een toe.</p>
          ) : dag.oefeningen.map((oe, oeIdx) => (
            <div key={oeIdx} className="rounded-xl border border-white/6 p-3 space-y-2" style={{ background: "#161616" }}>
              <div className="flex items-center gap-2">
                <Dumbbell size={12} style={{ color: GOLD }} className="shrink-0" />
                <input value={oe.naam} onChange={e => updateOefening(oeIdx, "naam", e.target.value)}
                  className="flex-1 bg-transparent text-white text-sm font-semibold focus:outline-none" />
                <button onClick={() => removeOefening(oeIdx)} className="text-zinc-700 hover:text-red-400 transition-colors">
                  <X size={13} />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[["sets", "Sets"], ["reps", "Reps"], ["rust", "Rust"]].map(([field, label]) => (
                  <div key={field}>
                    <p className="text-zinc-600 text-[10px] uppercase tracking-widest font-bold mb-1">{label}</p>
                    <input value={oe[field]} onChange={e => updateOefening(oeIdx, field, e.target.value)}
                      className="w-full bg-[#1a1a1a] border border-white/6 rounded-lg px-2 py-1.5 text-white text-sm font-medium focus:outline-none focus:border-[#C9A84C]/40"
                      placeholder={field === "rust" ? "90 sec" : field === "sets" ? "3" : "10"} />
                  </div>
                ))}
              </div>
              <div>
                <p className="text-zinc-600 text-[10px] uppercase tracking-widest font-bold mb-1">Notities (optioneel)</p>
                <input value={oe.notities || ""} onChange={e => updateOefening(oeIdx, "notities", e.target.value)}
                  className="w-full bg-[#1a1a1a] border border-white/6 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40"
                  placeholder="Uitvoeringstip, notitie..." />
              </div>
            </div>
          ))}

          <button onClick={() => setShowPicker(true)}
            className="w-full py-2.5 rounded-xl border border-dashed border-white/12 text-zinc-500 hover:text-white hover:border-white/25 transition-all flex items-center justify-center gap-2 text-sm font-medium">
            <Plus size={14} /> Oefening toevoegen
          </button>
        </div>
      )}

      {showPicker && (
        <ExercisePicker onSelect={addOefening} onClose={() => setShowPicker(false)} userToken={userToken} />
      )}
    </div>
  );
}

// ── Paywall ───────────────────────────────────────────────────────────────────
function SchemaMakerPaywall({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleUnlock = async () => {
    if (!user?.token) { navigate("/"); return; }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          naam: user.name || "FitSmith Gebruiker",
          email: user.email,
          product: "schema_builder_unlock",
        }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError("Betaling aanmaken mislukt. Probeer opnieuw.");
      }
    } catch {
      setError("Verbindingsfout. Probeer opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <button onClick={() => navigate("/account")}
          className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-8 transition-colors">
          <ArrowLeft size={15} /> Terug naar dashboard
        </button>

        <div className="rounded-2xl border overflow-hidden" style={{ background: "#111", borderColor: "rgba(201,168,76,0.2)" }}>
          <div className="px-8 py-8 text-center border-b border-white/6">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
              style={{ background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.2)" }}>
              <Lock size={24} style={{ color: GOLD }} />
            </div>
            <h1 className="text-white text-2xl font-black mb-2">Schema Maker</h1>
            <p className="text-zinc-400 text-sm">Maak onbeperkt je eigen trainingsschema's</p>
          </div>

          <div className="px-8 py-6 space-y-3">
            {[
              "Onbeperkt eigen schema's aanmaken",
              "Kies uit 188+ oefeningen of voeg eigen toe",
              "Privé — alleen jij ziet jouw schema's",
              "Direct loggen met de workout tracker",
              "Eenmalige betaling — lifetime toegang",
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle size={15} style={{ color: GOLD }} className="shrink-0" />
                <span className="text-zinc-300 text-sm">{f}</span>
              </div>
            ))}
          </div>

          <div className="px-8 pb-8">
            <div className="text-center mb-5">
              <span className="text-4xl font-black text-white">€100</span>
              <span className="text-zinc-500 text-sm ml-2">eenmalig</span>
              <p className="text-zinc-600 text-xs mt-1">Geen abonnement · Nooit meer betalen</p>
            </div>

            {error && <p className="text-red-400 text-sm text-center mb-4">{error}</p>}

            <button onClick={handleUnlock} disabled={loading}
              data-testid="unlock-schema-builder-btn"
              className="w-full py-4 rounded-2xl font-black text-base text-black flex items-center justify-center gap-2 disabled:opacity-60 transition-opacity"
              style={{ background: `linear-gradient(135deg, ${GOLD}, #D4AF37)` }}>
              <CreditCard size={18} />
              {loading ? "Doorsturen naar betaling..." : "Nu vrijzetten — €100"}
            </button>
            <p className="text-zinc-600 text-xs text-center mt-3">Veilig betalen via iDEAL · Directe toegang na betaling</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Schema Builder Page ──────────────────────────────────────────────────
export default function UserSchemaBuilderPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { schemaId } = useParams();

  const [access, setAccess] = useState(null); // null = laden
  const [schemas, setSchemas] = useState([]);
  const [activeSchema, setActiveSchema] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [toast, setToast] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Check toegang
  useEffect(() => {
    if (authLoading) return;
    if (!user?.token) { navigate("/"); return; }
    fetch(`${API}/api/schema-builder-access`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then(r => r.json())
      .then(d => setAccess(d))
      .catch(() => setAccess({ unlocked: false }));
  }, [user, authLoading, navigate]);

  const loadSchemas = useCallback(() => {
    if (!user?.token) return;
    fetch(`${API}/api/my-builder-schemas`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then(r => r.json())
      .then(d => setSchemas(d.schemas || []));
  }, [user]);

  useEffect(() => {
    if (access?.unlocked) loadSchemas();
  }, [access, loadSchemas]);

  // Open schema via URL param
  useEffect(() => {
    if (!schemaId || schemas.length === 0) return;
    const found = schemas.find(s => s.id === schemaId);
    if (found) setActiveSchema(JSON.parse(JSON.stringify(found)));
  }, [schemaId, schemas]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const handleNewSchema = () => {
    setActiveSchema({ id: null, naam: "", beschrijving: "", dagen: [] });
  };

  const handleSave = async () => {
    if (!activeSchema) return;
    setSaving(true);
    try {
      const body = {
        naam: activeSchema.naam || "Mijn schema",
        beschrijving: activeSchema.beschrijving || "",
        dagen: activeSchema.dagen,
      };
      let res;
      if (activeSchema.id) {
        res = await fetch(`${API}/api/my-builder-schemas/${activeSchema.id}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${user.token}`, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API}/api/my-builder-schemas`, {
          method: "POST",
          headers: { Authorization: `Bearer ${user.token}`, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.id) setActiveSchema(prev => ({ ...prev, id: data.id }));
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      loadSchemas();
      showToast("Schema opgeslagen!");
    } catch {
      showToast("Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    await fetch(`${API}/api/my-builder-schemas/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${user.token}` },
    });
    setDeleteConfirm(null);
    loadSchemas();
    if (activeSchema?.id === id) setActiveSchema(null);
    showToast("Schema verwijderd");
  };

  const addDag = () => {
    setActiveSchema(prev => ({
      ...prev,
      dagen: [...(prev.dagen || []), { dag_naam: `Dag ${(prev.dagen || []).length + 1}`, oefeningen: [] }]
    }));
  };

  const updateDag = (idx, updated) => {
    setActiveSchema(prev => ({ ...prev, dagen: prev.dagen.map((d, i) => i === idx ? updated : d) }));
  };

  const deleteDag = (idx) => {
    setActiveSchema(prev => ({ ...prev, dagen: prev.dagen.filter((_, i) => i !== idx) }));
  };

  // Loading states
  if (authLoading || access === null) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!access?.unlocked) {
    return <SchemaMakerPaywall user={user} />;
  }

  return (
    <div className="min-h-screen bg-[#080808]">
      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#111] border border-[#C9A84C]/40 text-white text-sm font-semibold px-5 py-3 rounded-xl shadow-2xl animate-fadeIn">
          {toast}
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-sm w-full mx-4">
            <p className="text-white font-bold mb-2">Schema verwijderen?</p>
            <p className="text-zinc-400 text-sm mb-5">"{deleteConfirm.naam}" wordt permanent verwijderd.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-300 text-sm font-semibold">
                Annuleren
              </button>
              <button onClick={() => handleDelete(deleteConfirm.id)}
                className="flex-1 py-2.5 rounded-xl bg-red-500/15 border border-red-500/30 text-red-400 text-sm font-semibold">
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-30 border-b border-white/5 backdrop-blur-md bg-[#080808]/90">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/account")}
            className="w-9 h-9 rounded-xl border border-white/8 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={16} />
          </button>
          <div className="flex-1">
            <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Schema Maker</p>
            {activeSchema && (
              <input
                value={activeSchema.naam}
                onChange={e => setActiveSchema(prev => ({ ...prev, naam: e.target.value }))}
                className="bg-transparent text-white font-bold text-base focus:outline-none w-full"
                placeholder="Naam van je schema"
                data-testid="schema-name-input"
              />
            )}
          </div>
          {activeSchema && (
            <button onClick={handleSave} disabled={saving}
              data-testid="save-schema-btn"
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${saved ? "text-green-400 border-green-500/30" : "border-[#C9A84C]/30 text-[#C9A84C]"} border`}>
              {saving ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> :
                saved ? <CheckCircle size={14} /> : <Save size={14} />}
              {saving ? "Opslaan..." : saved ? "Opgeslagen!" : "Opslaan"}
            </button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 flex gap-6">
        {/* Sidebar — schema lijst */}
        <div className="w-64 shrink-0 space-y-2">
          <button onClick={handleNewSchema}
            data-testid="new-schema-btn"
            className="w-full py-3 rounded-xl border border-dashed text-sm font-bold flex items-center justify-center gap-2 transition-all"
            style={{ borderColor: "rgba(201,168,76,0.3)", color: GOLD }}>
            <Plus size={15} /> Nieuw schema
          </button>

          {schemas.length === 0 && !activeSchema && (
            <div className="text-center py-8">
              <Dumbbell size={28} className="text-zinc-700 mx-auto mb-2" />
              <p className="text-zinc-600 text-sm">Nog geen schema's. Maak je eerste aan!</p>
            </div>
          )}

          {schemas.map(s => (
            <div key={s.id}
              className={`rounded-xl border p-3 cursor-pointer transition-all group ${
                activeSchema?.id === s.id
                  ? "border-[#C9A84C]/40 bg-[#C9A84C]/8"
                  : "border-white/6 hover:border-white/15"
              }`}
              onClick={() => setActiveSchema(JSON.parse(JSON.stringify(s)))}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className={`text-sm font-bold truncate ${activeSchema?.id === s.id ? "text-[#C9A84C]" : "text-white"}`}>
                    {s.naam || "Naamloos schema"}
                  </p>
                  <p className="text-zinc-600 text-xs mt-0.5">{s.dagen?.length || 0} dag{s.dagen?.length !== 1 ? "en" : ""}</p>
                </div>
                <button onClick={e => { e.stopPropagation(); setDeleteConfirm(s); }}
                  className="opacity-0 group-hover:opacity-100 text-zinc-700 hover:text-red-400 transition-all p-0.5">
                  <Trash2 size={13} />
                </button>
              </div>
              {activeSchema?.id === s.id && s.dagen?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {s.dagen.map((dag, i) => (
                    <button key={i}
                      onClick={e => {
                        e.stopPropagation();
                        navigate(`/workout-custom/${s.id}/${i}`);
                      }}
                      className="w-full flex items-center gap-1.5 text-xs text-zinc-400 hover:text-[#C9A84C] transition-colors py-0.5">
                      <Play size={10} /> {dag.dag_naam || `Dag ${i + 1}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Main editor */}
        <div className="flex-1 min-w-0">
          {!activeSchema ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Edit3 size={40} className="text-zinc-700 mb-4" />
              <p className="text-zinc-500 text-base font-semibold mb-2">Selecteer een schema of maak een nieuw schema aan</p>
              <p className="text-zinc-700 text-sm">Jouw schema's zijn privé en alleen voor jou zichtbaar</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Beschrijving */}
              <div className="rounded-2xl border border-white/6 px-5 py-4" style={{ background: "#0f0f0f" }}>
                <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-2">Beschrijving (optioneel)</p>
                <input
                  value={activeSchema.beschrijving || ""}
                  onChange={e => setActiveSchema(prev => ({ ...prev, beschrijving: e.target.value }))}
                  placeholder="Korte beschrijving van dit schema..."
                  className="w-full bg-transparent text-white text-sm focus:outline-none placeholder-zinc-700"
                />
              </div>

              {/* Dagen */}
              {(activeSchema.dagen || []).map((dag, i) => (
                <DagEditor key={i} dag={dag} dagIdx={i}
                  onUpdate={(updated) => updateDag(i, updated)}
                  onDelete={() => deleteDag(i)}
                  userToken={user.token}
                />
              ))}

              {/* Add dag */}
              <button onClick={addDag}
                data-testid="add-day-btn"
                className="w-full py-4 rounded-2xl border border-dashed border-white/10 text-zinc-500 hover:text-white hover:border-white/25 transition-all flex items-center justify-center gap-2 text-sm font-semibold">
                <Plus size={15} /> Trainingsdag toevoegen
              </button>

              {/* Save button */}
              <button onClick={handleSave} disabled={saving}
                className="w-full py-4 rounded-2xl font-black text-base text-black flex items-center justify-center gap-2"
                style={{ background: `linear-gradient(135deg, ${GOLD}, #D4AF37)` }}>
                <Save size={18} />
                {saving ? "Opslaan..." : "Schema opslaan"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
